import {useCallback,useState} from "react";
import {Control,FieldArrayWithId,UseFormHandleSubmit,UseFormSetValue,UseFormWatch,useFieldArray,useForm,} from "react-hook-form";
import {DayOfWeek,OpeningHourPeriod,OpeningHoursDay,} from "../types/modules/opening-hours.type";

export type OpeningPeriod = OpeningHourPeriod;
export type OpeningHoursFormDay = OpeningHoursDay;
export type OpeningHoursFormData = {days: OpeningHoursFormDay[];};
export type OpeningHoursApiDay = {
    day: DayOfWeek;
    periods?: {
        open?: string | null;
        close?: string | null;
    }[];
};

const DAYS_OF_WEEK: DayOfWeek[] = [
    "sun",
    "mon",
    "tue",
    "wed",
    "thu",
    "fri",
    "sat",
];

const DEFAULT_PERIOD: OpeningPeriod = {
    open: "00:00",
    close: "00:00",
};

const createDefaultDays =
    (): OpeningHoursFormDay[] => {
        return DAYS_OF_WEEK.map(
        (day) => ({
            day,
            periods: [],
        })
        );
    };

const createDefaultActiveDays =
    (): Record<
        DayOfWeek,
        boolean
    > => ({
        sun: false,
        mon: false,
        tue: false,
        wed: false,
        thu: false,
        fri: false,
        sat: false,
    });

const normalizeTime = (time?: string | null): string => {
    return time?.slice(0, 5) ?? "";
};

export const mapOpeningHoursToForm = (apiDays?: OpeningHoursApiDay[]): OpeningHoursFormDay[] => {
    return DAYS_OF_WEEK.map(
        (day) => {
        const apiDay =
            apiDays?.find(
            (item) =>
                item.day === day
            );

        return {
            day,

            periods:
            apiDay?.periods?.map(
                (period) => ({
                open: normalizeTime(
                    period.open
                ),

                close: normalizeTime(
                    period.close
                ),
                })
            ) ?? [],
        };
        }
    );
};

export const createOpeningHoursPayload =
    (
        data: OpeningHoursFormData,

        activeDays: Record<
        DayOfWeek,
        boolean
        >
    ) => {
        return {
        days: data.days.map(
            (day) => {
            /**
             * Closed day
             */
            if (
                !activeDays[
                day.day
                ]
            ) {
                return {
                day: day.day,
                periods: [],
                };
            }

            /**
             * Remove invalid periods
             */
            const periods =
                day.periods
                .filter(
                    (
                    period
                    ) =>
                    typeof period.open ===
                        "string" &&
                    period.open.trim() !==
                        "" &&
                    typeof period.close ===
                        "string" &&
                    period.close.trim() !==
                        ""
                )
                .map(
                    (
                    period
                    ) => ({
                    open: period.open.slice(
                        0,
                        5
                    ),

                    close: period.close.slice(
                        0,
                        5
                    ),
                    })
                );

            return {
                day: day.day,
                periods,
            };
            }
        ),
        };
    };


export interface UseOpeningHoursFormReturn {
    control: Control<OpeningHoursFormData>;
    fields: FieldArrayWithId< OpeningHoursFormData,"days","id">[];
    watch: UseFormWatch<OpeningHoursFormData>;
    setValue: UseFormSetValue<OpeningHoursFormData>;
    handleSubmit: UseFormHandleSubmit<OpeningHoursFormData>;
    activeDays: Record<DayOfWeek,boolean>;
    toggleDay: (day: DayOfWeek) => void;
    addPeriod: (dayIndex: number) => void;
    removePeriod: (dayIndex: number,periodIndex: number) => void;
    applyToAllDays: () => void;
    handleApplyToggle: () => void;
    setOpeningHours: (days?: OpeningHoursApiDay[]) => void;
    buildPayload: (data: OpeningHoursFormData) => ReturnType<typeof createOpeningHoursPayload>;
    applied: boolean;
}

const useOpeningHoursForm =
    (): UseOpeningHoursFormReturn => {
        const {
        control,
        watch,
        setValue,
        handleSubmit,
        } =
        useForm<OpeningHoursFormData>(
            {
            defaultValues: {
                days:
                createDefaultDays(),
            },
            }
        );

    const {fields,replace,} =useFieldArray<OpeningHoursFormData,"days","id">({control,name: "days",});
    const [activeDays,setActiveDays,] =useState<Record<DayOfWeek,boolean>>(createDefaultActiveDays());
    const [applied,setApplied,] =useState(false);

        const setOpeningHours =
        useCallback(
            (
            apiDays?: OpeningHoursApiDay[]
            ) => {
            const days =
                mapOpeningHoursToForm(
                apiDays
                );

            replace(days);

            const active =
                createDefaultActiveDays();

            days.forEach(
                (day) => {
                active[
                    day.day
                ] =
                    day.periods.length >
                    0;
                }
            );

            setActiveDays(
                active
            );

            setApplied(false);
            },
            [replace]
        );

    const toggleDay =
        useCallback(
            (
            day: DayOfWeek
            ) => {
            const index =
                fields.findIndex(
                (field) =>
                    field.day ===
                    day
                );

            if (index === -1) {
                return;
            }

            const isActive =
                !activeDays[day];

            if (!isActive) {
                setValue(
                `days.${index}.periods`,
                [],
                {
                    shouldDirty:
                    true,
                    shouldValidate:
                    true,
                }
                );
            }

            else {
                const periods =
                watch(
                    `days.${index}.periods`
                ) ?? [];

                if (
                periods.length ===
                0
                ) {
                setValue(
                    `days.${index}.periods`,
                    [
                    {
                        ...DEFAULT_PERIOD,
                    },
                    ],
                    {
                    shouldDirty:
                        true,
                    shouldValidate:
                        true,
                    }
                );
                }
            }

            setActiveDays(
                (prev) => ({
                ...prev,

                [day]:
                    isActive,
                })
            );
            },
            [
            fields,
            activeDays,
            setValue,
            watch,
            ]
        );


    const addPeriod =
        useCallback(
            (
            dayIndex: number
            ) => {
            const periods =
                watch(
                `days.${dayIndex}.periods`
                ) ?? [];

            setValue(
                `days.${dayIndex}.periods`,
                [
                ...periods,

                {
                    ...DEFAULT_PERIOD,
                },
                ],
                {
                shouldDirty:
                    true,

                shouldValidate:
                    true,
                }
            );
            },
            [
            watch,
            setValue,
            ]
        );

        const removePeriod =
        useCallback(
            (
            dayIndex: number,

            periodIndex: number
            ) => {
            const periods =
                watch(
                `days.${dayIndex}.periods`
                ) ?? [];

            setValue(
                `days.${dayIndex}.periods`,
                periods.filter(
                (
                    _period,
                    index
                ) =>
                    index !==
                    periodIndex
                ),
                {
                shouldDirty:
                    true,

                shouldValidate:
                    true,
                }
            );
            },
            [
            watch,
            setValue,
            ]
        );

        const applyToAllDays =
        useCallback(() => {
            const firstActiveIndex =
            fields.findIndex(
                (field) =>
                activeDays[
                    field.day
                ]
            );

            if (
            firstActiveIndex ===
            -1
            ) {
            return;
            }

            const sourcePeriods =
            watch(
                `days.${firstActiveIndex}.periods`
            ) ?? [];

            if (
            sourcePeriods.length ===
            0
            ) {
            return;
            }

            fields.forEach(
            (
                field,
                index
            ) => {
                if (
                index ===
                firstActiveIndex
                ) {
                return;
                }

                setValue(
                `days.${index}.periods`,
                sourcePeriods.map(
                    (
                    period
                    ) => ({
                    open:
                        period.open ||
                        "00:00",

                    close:
                        period.close ||
                        "00:00",
                    })
                ),
                {
                    shouldDirty:
                    true,

                    shouldValidate:
                    true,
                }
                );
            }
            );

            setActiveDays(
            (prev) => {
                const updated =
                {
                    ...prev,
                };

                fields.forEach(
                (
                    field
                ) => {
                    updated[
                    field.day
                    ] = true;
                }
                );

                return updated;
            }
            );

            setApplied(true);
        }, [
            fields,
            activeDays,
            watch,
            setValue,
        ]);


        const handleApplyToggle =
        useCallback(() => {
            if (!applied) {
            applyToAllDays();
            } else {
            setApplied(false);
            }
        }, [
            applied,
            applyToAllDays,
        ]);

        const buildPayload =
        useCallback(
            (
            data: OpeningHoursFormData
            ) => {
            return createOpeningHoursPayload(
                data,
                activeDays
            );
            },
            [activeDays]
        );

    return {
        control,
        fields,
        watch,
        setValue,
        handleSubmit,
        activeDays,
        toggleDay,
        addPeriod,
        removePeriod,
        applyToAllDays,
        handleApplyToggle,
        setOpeningHours,
        buildPayload,
        applied,
        };
    };

export default useOpeningHoursForm;